import { ReactElement, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'

import {
  manageCardHoldersModalContainerId,
  reportLostOrStolenCardModalContainerId
} from 'const/modalContainerId'
import i18n from 'strings/i18n'
import {
  ModalHeader,
  SlideInModalPortal
} from 'syf-component-library/ui/patterns/SlideInModalPortal'

import {
  AuthenticationRedirectProps,
  handleMFEAuthenticationRedirect
} from 'helpers/authenticationRedirect'
import routes from 'const/routes'
import ManageCardHolders from '@account-services-mfe/manage-cardholders'
import getMFEConfig from 'helpers/getMFEConfig'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import useContainerElement from 'hooks/useContainerElement'

const ManageCardholderModal = (): ReactElement => {
  const [{ modalId, modalTriggerId }, updateModalStatus] =
    useAtom(modalStatusAtom)
  const headerRef = useRef(null)
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const mfeConfig = getMFEConfig('mfe')

  const containerId = manageCardHoldersModalContainerId(selectedAccount)
  const isParentElementReady = useContainerElement(containerId)

  const handleCloseModal = () => {
    updateModalStatus('')
  }

  const handleEscapeAndModalHeader = () => {
    triggerAnalyticsEvent({
      pageFunction: 'account services',
      pageKind: 'account services'
    })
    handleCloseModal()
  }

  const handleReportLostStolen = () => {
    updateModalStatus(
      'ReportLostOrStolenCard',
      reportLostOrStolenCardModalContainerId(selectedAccount)
    )
  }

  const handleAuthFlow = () => {
    const props: AuthenticationRedirectProps = {
      navigateTo: routes.ACCOUNT_SERVICES,
      modal: 'ManageCardholders',
      scope: 'read write write:sensitive'
    }

    handleMFEAuthenticationRedirect(props)
  }

  return (
    modalId === 'ManageCardholders' &&
    isParentElementReady && (
      <SlideInModalPortal
        containerId={containerId}
        escapeModalHandler={handleEscapeAndModalHeader}
        id={`mch-slide-in-modal-${selectedAccount}`}
        ariaLabel="manage cardholders"
        isOpen={modalId === 'ManageCardholders'}
        modalTriggerId={modalTriggerId}
        header={
          <ModalHeader
            closeModal={handleEscapeAndModalHeader}
            content={i18n({ modal: 'manageCardholdersTitle' }) as string}
            data-test="mch-modal-header"
            dataReason="x button"
            dataType="manage cardholders"
            headerRef={headerRef}
          />
        }
      >
        <ManageCardHolders
          authenticationRedirect={handleAuthFlow}
          config={mfeConfig}
          handleClose={handleCloseModal}
          handleReportLostStolen={handleReportLostStolen}
          selectedAccountId={selectedAccount}
          features={{
            brandRefreshPhase: getBrandRefreshPhase(),
            isRemoveAuthUserEnabled: isFeatureFlagEnabled(
              'isRemoveAuthUserEnabled'
            )
          }}
        />
      </SlideInModalPortal>
    )
  )
}

export default ManageCardholderModal
